import React from 'react';
import { MapPin, Plus } from 'lucide-react';

/**
 * Компонент для отображения списка популярных городов для быстрого добавления
 */
const PopularCitiesSection = ({ onSelectCity }) => {
  // Категории городов
  const cityCategories = [
    {
      name: 'Крупные города России',
      cities: [
        { name: 'Москва', timezone: 'Europe/Moscow', country: 'Россия', flag: '🇷🇺' },
        { name: 'Санкт-Петербург', timezone: 'Europe/Moscow', country: 'Россия', flag: '🇷🇺' },
        { name: 'Екатеринбург', timezone: 'Asia/Yekaterinburg', country: 'Россия', flag: '🇷🇺' },
        { name: 'Новосибирск', timezone: 'Asia/Novosibirsk', country: 'Россия', flag: '🇷🇺' },
        { name: 'Владивосток', timezone: 'Asia/Vladivostok', country: 'Россия', flag: '🇷🇺' },
        { name: 'Калининград', timezone: 'Europe/Kaliningrad', country: 'Россия', flag: '🇷🇺' }
      ]
    },
    {
      name: 'Бизнес-центры мира',
      cities: [
        { name: 'Нью-Йорк', timezone: 'America/New_York', country: 'США', flag: '🇺🇸' },
        { name: 'Лондон', timezone: 'Europe/London', country: 'Великобритания', flag: '🇬🇧' },
        { name: 'Токио', timezone: 'Asia/Tokyo', country: 'Япония', flag: '🇯🇵' },
        { name: 'Шанхай', timezone: 'Asia/Shanghai', country: 'Китай', flag: '🇨🇳' },
        { name: 'Сингапур', timezone: 'Asia/Singapore', country: 'Сингапур', flag: '🇸🇬' },
        { name: 'Гонконг', timezone: 'Asia/Hong_Kong', country: 'Китай (САР)', flag: '🇭🇰' }
      ]
    },
    {
      name: 'Страны СНГ',
      cities: [
        { name: 'Минск', timezone: 'Europe/Minsk', country: 'Беларусь', flag: '🇧🇾' },
        { name: 'Киев', timezone: 'Europe/Kiev', country: 'Украина', flag: '🇺🇦' },
        { name: 'Нур-Султан', timezone: 'Asia/Almaty', country: 'Казахстан', flag: '🇰🇿' },
        { name: 'Ташкент', timezone: 'Asia/Tashkent', country: 'Узбекистан', flag: '🇺🇿' },
        { name: 'Баку', timezone: 'Asia/Baku', country: 'Азербайджан', flag: '🇦🇿' },
        { name: 'Ереван', timezone: 'Asia/Yerevan', country: 'Армения', flag: '🇦🇲' }
      ]
    },
    {
      name: 'Европейские города',
      cities: [
        { name: 'Берлин', timezone: 'Europe/Berlin', country: 'Германия', flag: '🇩🇪' },
        { name: 'Париж', timezone: 'Europe/Paris', country: 'Франция', flag: '🇫🇷' },
        { name: 'Рим', timezone: 'Europe/Rome', country: 'Италия', flag: '🇮🇹' },
        { name: 'Мадрид', timezone: 'Europe/Madrid', country: 'Испания', flag: '🇪🇸' },
        { name: 'Стокгольм', timezone: 'Europe/Stockholm', country: 'Швеция', flag: '🇸🇪' },
        { name: 'Амстердам', timezone: 'Europe/Amsterdam', country: 'Нидерланды', flag: '🇳🇱' }
      ]
    }
  ];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 mb-8">
      <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white flex items-center">
        <MapPin className="w-5 h-5 mr-2 text-indigo-500" />
        Популярные города
      </h2>

      <div className="space-y-6">
        {cityCategories.map((category, categoryIndex) => (
          <div key={categoryIndex}>
            <h3 className="text-base font-medium text-gray-800 dark:text-gray-200 mb-3">
              {category.name}
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
              {category.cities.map((city, cityIndex) => (
                <button
                  key={cityIndex}
                  type="button"
                  onClick={() => onSelectCity(city)}
                  className="flex items-center justify-between px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 border border-gray-200 dark:border-gray-600 rounded-lg transition-colors group"
                >
                  <div className="flex items-center">
                    <span className="text-lg mr-2">{city.flag}</span>
                    <span className="font-medium text-gray-700 dark:text-gray-300">{city.name}</span>
                  </div>
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity">
                    <Plus className="w-4 h-4 text-indigo-500" />
                  </span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
        <div className="text-sm text-blue-700 dark:text-blue-300 font-medium mb-2">Страны по количеству часовых поясов:</div>
        <div className="grid grid-cols-2 gap-2 text-sm text-blue-600 dark:text-blue-400">
          <div className="flex justify-between">
            <span>Россия</span>
            <span className="font-medium">11 поясов</span>
          </div>
          <div className="flex justify-between">
            <span>Франция (с территориями)</span>
            <span className="font-medium">12 поясов</span>
          </div>
          <div className="flex justify-between">
            <span>США</span>
            <span className="font-medium">6 поясов</span>
          </div>
          <div className="flex justify-between">
            <span>Австралия</span>
            <span className="font-medium">5 поясов</span>
          </div>
          <div className="flex justify-between">
            <span>Канада</span>
            <span className="font-medium">6 поясов</span>
          </div>
          <div className="flex justify-between">
            <span>Бразилия</span>
            <span className="font-medium">4 пояса</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PopularCitiesSection;